import React from 'react'
import Button from 'components/button'
import Tooltip from 'components/tooltip'

export interface ToggleGroupItem {
  value: string
  /** The item's accessible name, and its tooltip. */
  label: string
  /** What the button shows: an icon, or a mark as short as one, such as "A.". */
  content: React.ReactNode
}

interface ToggleGroupProps {
  label: string
  items: ToggleGroupItem[]
  /** The values pressed: one for a choice between them, any number for independent toggles. */
  pressed: string[]
  onToggle: (value: string) => void
}

/**
 * A row of toggles, in the manner of shadcn/ui's outline toggle: 32px squares, the height of a menu
 * row and of the other controls in a popover, a little apart, blue while pressed, each named by a
 * tooltip. Every item is an icon or a mark of a character or two, so every button is the same square.
 */
const ToggleGroup: React.FC<ToggleGroupProps> = ({ label, items, pressed, onToggle }) => (
  <div role="group" aria-label={label} className="flex items-center gap-1">
    {items.map((item) => {
      const on = pressed.includes(item.value)
      return (
        <Tooltip key={item.value} content={item.label}>
          <Button
            internalType="outline"
            aria-label={item.label}
            aria-pressed={on}
            // Heavier strokes than the app's 2px icons, and the fields' text colour while off, so the
            // marks read as clearly as the values in the fields beside them.
            className={`size-8 rounded-md p-0 font-medium [&>svg]:stroke-[2.5] ${
              on ? 'border-blue/40 bg-blue/10 text-blue hover:bg-blue/15' : 'border-divider text-body'
            }`}
            onClick={() => onToggle(item.value)}
          >
            {item.content}
          </Button>
        </Tooltip>
      )
    })}
  </div>
)

export default ToggleGroup
